import React from 'react';
import I18n from 'i18n-js';
import classnames from 'classnames';

import { core_apimessages_ShippingLabelPackage } from '../../../schemas/rql';
import { MeasurementSystem } from './shipping_label_package';
import PackageInput from './package_input';
import HelpCard from '../help_card';
import DimensionIllustration from '../../../../images/shipping/box-dimension-illustration.svg';
import ShippingBox from '../../../../images/shipping/shipping-box.svg';

import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import bind from '@reverbdotcom/commons/src/bind';
import ControlledInput from '@reverbdotcom/commons/src/components/controlled_input';
import { RCAlertBox } from '@reverbdotcom/cadence/components';

export enum PackageAttribute {
  HEIGHT = 'height',
  LENGTH = 'length',
  WIDTH = 'width',
  WEIGHT = 'weight',
}

const CARRIER_CALCULATED_FORM_PREFIX = 'product[shipping][carrier_calculated_package]';

interface IProps {
  shippingPackage: core_apimessages_ShippingLabelPackage;
  measurementSystem: MeasurementSystem;
  updatePackageDimensions: (attributes: core_apimessages_ShippingLabelPackage) => void;
  updateMeasurementSystem?: (measurementSystem: MeasurementSystem) => void;
  hideMeasurementSystemControls?: boolean;
  forCarrierCalculated?: boolean;
  disabled?: boolean;
}

export default class CustomPackageInputs extends React.Component<IProps> {
  @bind
  updateMeasurementSystem(attributes) {
    this.props.updateMeasurementSystem(attributes.measurementSystem);
  }

  @bind
  usingMeasurementSystem(measurementSystem: MeasurementSystem): boolean {
    return this.props.measurementSystem === measurementSystem;
  }

  buildDimensionInputName(inputName: PackageAttribute): string {
    if (this.props.forCarrierCalculated) {
      return `${CARRIER_CALCULATED_FORM_PREFIX}[${inputName}_value]`;
    }

    return `shipping_label_form[package][${inputName}]`;
  }

  render() {
    return (
      <div className="g-container g-container--cycle g-container--flex mobile-fx-dir-col">
        <div className="g-col-mobile-12 g-col-7">
          {this.props.forCarrierCalculated &&
            <p>
              <strong>{I18n.t('discovery.dashboard.selling.shippingLabels.package.dimensionsRequest')}</strong><br />
              {I18n.t('discovery.dashboard.selling.shippingLabels.package.dimensionsRequestReason')}
            </p>
          }
          {!this.props.hideMeasurementSystemControls &&
            <>
              <div className="label-with-radio scaling-mb-1">
                <ControlledInput
                  inputType="radio"
                  inputName="measurementSystemImperial"
                  inputValue={MeasurementSystem.IMPERIAL}
                  checked={this.usingMeasurementSystem(MeasurementSystem.IMPERIAL)}
                  updateField={this.updateMeasurementSystem}
                  fieldName="measurementSystem"
                  disabled={this.props.disabled}
                />
                <label htmlFor="measurementSystemImperial">
                  {I18n.t('discovery.dashboard.selling.shippingLabels.package.useImperial')}
                </label>
              </div>
              <div className="label-with-radio">
                <ControlledInput
                  inputType="radio"
                  inputName="measurementSystemMetric"
                  inputValue={MeasurementSystem.METRIC}
                  checked={this.usingMeasurementSystem(MeasurementSystem.METRIC)}
                  updateField={this.updateMeasurementSystem}
                  fieldName="measurementSystem"
                  disabled={this.props.disabled}
                />
                <label htmlFor="measurementSystemMetric">
                  {I18n.t('discovery.dashboard.selling.shippingLabels.package.useMetric')}
                </label>
              </div>
            </>
          }
          <div className="mtb-2">
            <RCAlertBox type="warning" small>
              <p className="mb-0 lh-120">
                <SanitizedRender
                  html={I18n.t('discovery.dashboard.selling.shippingLabels.package.inaccurateDimensionsWarningHtml', {
                    linkOpen:
                      '<a href="https://help.reverb.com/hc/articles/360013150694-Why-Did-I-Receive-a-Shipping-Label-Adjustment-#Avoid" target="_blank" rel="noopener noreferrer" tabIndex="-1">',
                    linkClose: '</a>',
                  })}
                />
              </p>
            </RCAlertBox>
          </div>
          <div className="mb-2">
            <PackageInput
              inputName={this.buildDimensionInputName(PackageAttribute.LENGTH)}
              fieldName={PackageAttribute.LENGTH}
              value={this.props.shippingPackage.length.value}
              shippingPackage={this.props.shippingPackage}
              titleKey={PackageAttribute.LENGTH}
              measurementUnit={this.props.shippingPackage.length.unit}
              onUpdate={this.props.updatePackageDimensions}
              disabled={this.props.disabled}
              roundUpUserInput
            />
          </div>
          <div className="mb-2">
            <PackageInput
              inputName={this.buildDimensionInputName(PackageAttribute.WIDTH)}
              fieldName={PackageAttribute.WIDTH}
              value={this.props.shippingPackage.width.value}
              shippingPackage={this.props.shippingPackage}
              titleKey={PackageAttribute.WIDTH}
              measurementUnit={this.props.shippingPackage.width.unit}
              onUpdate={this.props.updatePackageDimensions}
              disabled={this.props.disabled}
              roundUpUserInput
            />
          </div>
          <div className="mb-2">
            <PackageInput
              inputName={this.buildDimensionInputName(PackageAttribute.HEIGHT)}
              fieldName={PackageAttribute.HEIGHT}
              value={this.props.shippingPackage.height.value}
              shippingPackage={this.props.shippingPackage}
              titleKey={PackageAttribute.HEIGHT}
              measurementUnit={this.props.shippingPackage.height.unit}
              onUpdate={this.props.updatePackageDimensions}
              disabled={this.props.disabled}
              roundUpUserInput
            />
          </div>
          <div className="mb-2">
            <PackageInput
              inputName={this.buildDimensionInputName(PackageAttribute.WEIGHT)}
              fieldName={PackageAttribute.WEIGHT}
              value={this.props.shippingPackage.weight.value}
              shippingPackage={this.props.shippingPackage}
              titleKey={`${PackageAttribute.WEIGHT}Title`}
              subtitleKey={this.props.measurementSystem === MeasurementSystem.IMPERIAL && 'imperialOuncesHint'}
              measurementUnit={this.props.shippingPackage.weight.unit}
              onUpdate={this.props.updatePackageDimensions}
              disabled={this.props.disabled}
            />
          </div>
          {this.props.forCarrierCalculated &&
            <>
              <input
                value={this.props.shippingPackage.length.unit}
                type="hidden"
                name={`${CARRIER_CALCULATED_FORM_PREFIX}[length_unit]`}
                id={`${CARRIER_CALCULATED_FORM_PREFIX}[length_unit]`}
              />
              <input
                value={this.props.shippingPackage.weight.unit}
                type="hidden"
                name={`${CARRIER_CALCULATED_FORM_PREFIX}[weight_unit]`}
                id={`${CARRIER_CALCULATED_FORM_PREFIX}[weight_unit]`}
              />
            </>
          }
        </div>
        <div className="g-col-mobile-12 g-col-5 d-flex">
          <div className={classnames(
            'fx-align-self-end mobile-fx-width-100 mb-4 mobile-mb-0 mobile-mt-3',
            {
              'fx-width-70': !this.props.forCarrierCalculated,
              'mt-5': this.props.forCarrierCalculated,
            },
          )}>
            <HelpCard
              svgIcon={ShippingBox}
              iconAlt={I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.shippingBoxImgAlt')}
            >
              <p className="weight-bold">
                {I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.makeSure')}
              </p>
              <ul className="rsl-selector-module__list-group">
                <li className="rsl-selector-module__list-item">
                  {this.props.measurementSystem === MeasurementSystem.IMPERIAL ?
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.roundUpImperial') :
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.roundUpMetric')
                  }
                </li>
                <li className="rsl-selector-module__list-item">
                  {this.props.measurementSystem === MeasurementSystem.IMPERIAL ?
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.addImperial') :
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.addMetric')
                  }
                </li>
              </ul>
              <div className="d-flex fx-justify-center pt-2">
                <img
                  src={DimensionIllustration}
                  alt={I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.boxIllustrationImgAlt')}
                  className="width-80"
                />
              </div>
            </HelpCard>
          </div>
        </div>
      </div>
    );
  }
}
